import React, { ReactNode, useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { agentConversationsService } from '../services/agentConversationsService';
import { AgentConversationsProvider } from './AgentConversationsContext';
import { ClinicAgentProvider } from './ClinicAgentContext';

// Picks the agent chat the backend supports. A backend with conversations
// answers GET /api/clinic/agent/conversations/; one without it returns 404 and
// the clinic keeps the single-thread chat (ClinicAgentProvider), whose
// messages that backend still accepts. Remove the fallback once every backend
// the clinic talks to has conversations.

const CLINICIAN_ROLES = ['doctor', 'nutritionist', 'admin'];

type Mode = 'probing' | 'conversations' | 'single-thread';

export const ClinicAgentRoot = ({ children }: { children: ReactNode }) => {
  const { user, access } = useAuth();
  const isClinician = !!user && CLINICIAN_ROLES.includes(user.role);
  const [mode, setMode] = useState<Mode>('probing');

  useEffect(() => {
    if (!access || !isClinician) return;
    let cancelled = false;
    agentConversationsService
      .isAvailable()
      .then((available) => {
        if (!cancelled) setMode(available ? 'conversations' : 'single-thread');
      })
      .catch((err) => {
        // Unreachable or failing: the single-thread chat still works against
        // either backend, so it is the safe choice for this session.
        console.warn('ClinicAgentRoot: could not check for conversations', err);
        if (!cancelled) setMode('single-thread');
      });
    return () => {
      cancelled = true;
    };
  }, [access, isClinician]);

  // Always the same two elements: only `enabled` changes when the check
  // answers, so the page below is never remounted.
  return (
    <AgentConversationsProvider enabled={isClinician && mode === 'conversations'}>
      <ClinicAgentProvider enabled={isClinician && mode === 'single-thread'}>{children}</ClinicAgentProvider>
    </AgentConversationsProvider>
  );
};
